<template>
  <div class="tt-page">
    <TripPageHeader
      title="Trip Settings"
      subtitle="Configure office origin and the CAO name shown on Driver’s Tickets"
      icon="bi-gear"
    >
      <template #actions>
        <button class="btn btn-sm tt-btn-primary" :disabled="saving" @click="save">
          <span v-if="saving" class="spinner-border spinner-border-sm mr-1"></span>
          <i v-else class="bi bi-check2 mr-1"></i>Save settings
        </button>
      </template>
    </TripPageHeader>

    <TripSettingsNav />

    <div v-if="loading" class="tt-empty">
      <span class="spinner-border spinner-border-sm mr-2"></span>Loading settings…
    </div>

    <template v-else>
      <div class="tt-card mb-3">
        <div class="tt-card-body">
          <h6 class="tt-section-title">Print signatory</h6>
          <p class="small text-muted mb-3">
            Name and title printed under “Authorized by” on the Driver’s Ticket.
            If a trip has already been approved, the approving user’s name is used instead.
          </p>
          <div class="form-group">
            <label>Approver name <span class="text-danger">*</span></label>
            <input
              v-model="form.approver_name"
              class="form-control"
              placeholder="e.g. NOEME ROSE D. MANI, MM, CPA"
            />
          </div>
          <div class="form-group mb-0">
            <label>Approver title <span class="text-danger">*</span></label>
            <input
              v-model="form.approver_title"
              class="form-control"
              placeholder="e.g. CHIEF ADMINISTRATIVE OFFICER"
            />
          </div>
        </div>
      </div>

      <div class="tt-card">
        <div class="tt-card-body">
          <h6 class="tt-section-title">Office origin</h6>
          <p class="small text-muted mb-3">
            This is the starting point for destination and trip route calculations
            (origin → destination → back to origin). New destinations pre-fill with this location.
            Multi-stop legs are added on the trip request.
          </p>

          <div class="form-group">
            <label>Office name <span class="text-danger">*</span></label>
            <input v-model="form.origin_label" class="form-control" placeholder="e.g. CHED Caraga Regional Office" />
          </div>

          <div class="form-group mb-2">
            <label>Search office location <span class="text-danger">*</span></label>
            <LocationSearch
              :value="searchLabel"
              placeholder="Search office address…"
              @input="(v) => (searchLabel = v)"
              @selected="applyLocation"
            />
            <small class="text-muted">Pick from search results — coordinates are filled automatically.</small>
          </div>

          <div v-if="form.origin_lat && form.origin_lng" class="tt-location-set mb-3">
            <i class="bi bi-check-circle-fill text-success mr-1"></i>
            Location set from search
          </div>
          <div v-else class="tt-alert mb-3">
            Search and select the office address above. You do not need to type latitude or longitude.
          </div>

          <LocationMapPreview
            v-if="!loading && form.origin_lat && form.origin_lng"
            :dest-lat="form.origin_lat"
            :dest-lng="form.origin_lng"
            :dest-label="form.origin_label || 'Office'"
            dest-letter="A"
            height="260px"
            @dest-moved="onOriginMoved"
          />

          <p v-if="updatedAt" class="small text-muted mb-0">
            Last updated {{ fmtDt(updatedAt) }}
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from "axios";
import TripPageHeader from "./components/TripPageHeader.vue";
import TripSettingsNav from "./components/TripSettingsNav.vue";
import LocationSearch from "./components/LocationSearch.vue";
import LocationMapPreview from "./components/LocationMapPreview.vue";

export default {
  name: "TripSettings",
  components: { TripPageHeader, TripSettingsNav, LocationSearch, LocationMapPreview },
  data() {
    return {
      loading: true,
      saving: false,
      updatedAt: null,
      searchLabel: "",
      form: {
        origin_label: "",
        origin_lat: "",
        origin_lng: "",
        approver_name: "",
        approver_title: "",
      },
    };
  },
  methods: {
    err(e) {
      const d = e?.response?.data;
      if (d?.errors) return Object.values(d.errors).flat()[0];
      return d?.message || "Request failed.";
    },
    fmtDt(v) {
      if (!v) return "";
      return new Date(v).toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" });
    },
    applyLocation(place) {
      this.searchLabel = place.display_name;
      this.form.origin_lat = place.lat;
      this.form.origin_lng = place.lng;
      if (!this.form.origin_label) {
        this.form.origin_label = place.display_name.split(",")[0];
      }
    },
    onOriginMoved({ lat, lng }) {
      this.form.origin_lat = lat;
      this.form.origin_lng = lng;
    },
    load() {
      this.loading = true;
      axios
        .get("api/trips/settings")
        .then((r) => {
          const origin = r.data.origin || {};
          const approver = r.data.approver || {};
          this.form = {
            origin_label: origin.label || "",
            origin_lat: origin.lat ?? "",
            origin_lng: origin.lng ?? "",
            approver_name: approver.name || "",
            approver_title: approver.title || "",
          };
          this.searchLabel = origin.label || "";
          this.updatedAt = r.data.updated_at;
        })
        .catch((e) => toastr.error(this.err(e), "Error"))
        .finally(() => {
          this.loading = false;
        });
    },
    save() {
      if (!this.form.approver_name || !(this.form.approver_name || "").trim()) {
        toastr.error("Approver name is required.", "Validation");
        return;
      }
      if (!this.form.approver_title || !(this.form.approver_title || "").trim()) {
        toastr.error("Approver title is required.", "Validation");
        return;
      }
      if (!this.form.origin_label || !(this.form.origin_label || "").trim()) {
        toastr.error("Office name is required.", "Validation");
        return;
      }
      if (!this.form.origin_lat || !this.form.origin_lng) {
        toastr.error("Search and select the office location first.", "Validation");
        return;
      }
      this.saving = true;
      axios
        .put("api/trips/settings", this.form)
        .then((r) => {
          this.updatedAt = r.data.updated_at;
          const approver = r.data.approver || {};
          this.form.approver_name = approver.name || this.form.approver_name;
          this.form.approver_title = approver.title || this.form.approver_title;
          toastr.success("Trip settings saved.", "Success");
        })
        .catch((e) => toastr.error(this.err(e), "Error"))
        .finally(() => {
          this.saving = false;
        });
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.tt-location-set {
  font-size: 0.875rem;
  color: #334155;
  background: #f8fafc;
  border: 1px solid #e8ecf4;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
}
</style>
